import { diffWords, type DiffSegment } from "./markup";
import {
  docToPlainText,
  textDoc,
  type RichTextDoc,
  type RichTextMark,
  type RichTextNode,
} from "./richText";

/**
 * Turn before/after legislative text into the display-only "Updates" markup
 * document: deletions are struck through and insertions are bold red in the
 * read-only renderer and email renderer.
 */

const amendmentMarkFor = (kind: DiffSegment["kind"]): "deletion" | "insertion" | null => {
  switch (kind) {
    case "delete":
      return "deletion";
    case "insert":
      return "insertion";
    case "equal":
      return null;
  }
};

const withoutAmendmentMarks = (marks: readonly RichTextMark[] | undefined): RichTextMark[] =>
  (marks ?? []).filter((mark) => mark.type !== "deletion" && mark.type !== "insertion");

const marksFor = (
  kind: DiffSegment["kind"],
  baseMarks: readonly RichTextMark[] | undefined = [],
): RichTextMark[] => {
  const marks = withoutAmendmentMarks(baseMarks);
  const amendmentMark = amendmentMarkFor(kind);
  return amendmentMark === null ? marks : [...marks, { type: amendmentMark }];
};

const textNode = (
  text: string,
  kind: DiffSegment["kind"],
  baseMarks?: readonly RichTextMark[],
): RichTextNode | null => {
  if (text.length === 0) return null;
  const marks = marksFor(kind, baseMarks);
  return marks.length > 0 ? { type: "text", text, marks } : { type: "text", text };
};

const appendTextNode = (
  nodes: RichTextNode[],
  text: string,
  kind: DiffSegment["kind"],
  baseMarks?: readonly RichTextMark[],
): void => {
  const node = textNode(text, kind, baseMarks);
  if (node !== null) nodes.push(node);
};

const HORIZONTAL_LAYOUT_SPACE = String.raw`[^\S\r\n]`;
const LINE_BREAK_PATTERN = String.raw`(?:\r\n?|\n)`;
const LINE_BREAK_RE = /\r\n?|\n/;
const STRUCTURAL_BREAK_RE = new RegExp(
  String.raw`${LINE_BREAK_PATTERN}(?:${HORIZONTAL_LAYOUT_SPACE}*${LINE_BREAK_PATTERN})*${HORIZONTAL_LAYOUT_SPACE}*(?=(?:\([0-9A-Za-z]+\)|[0-9]+[.)]|[A-Za-z][.)])(?:\s|$))`,
  "g",
);
const STRUCTURAL_START_RE = new RegExp(
  String.raw`^${HORIZONTAL_LAYOUT_SPACE}*(?:\([0-9A-Za-z]+\)|[0-9]+[.)]|[A-Za-z][.)])(?:\s|$)`,
);
const LEADING_LAYOUT_SPACE_RE = new RegExp(String.raw`^${HORIZONTAL_LAYOUT_SPACE}+`);

type StructuralBreakRange = {
  readonly start: number;
  readonly end: number;
};

const cloneTextWith = (node: RichTextNode, text: string): RichTextNode | null =>
  text.length === 0 ? null : { ...node, text };

const appendTextWithHardBreaks = (
  nodes: RichTextNode[],
  node: RichTextNode,
  text: string,
): void => {
  if (text.length === 0) return;
  const parts = text.split(LINE_BREAK_RE);
  for (const [index, part] of parts.entries()) {
    if (index > 0) nodes.push({ type: "hardBreak" });
    const next = cloneTextWith(node, part);
    if (next !== null) nodes.push(next);
  }
};

const normalizeInlineLineBreaks = (nodes: readonly RichTextNode[]): RichTextNode[] =>
  nodes.flatMap((node) => {
    if (node.type !== "text" || node.text === undefined || !LINE_BREAK_RE.test(node.text)) return [node];
    const out: RichTextNode[] = [];
    appendTextWithHardBreaks(out, node, node.text);
    return out;
  });

const trimTrailingLayoutBreaks = (nodes: readonly RichTextNode[]): RichTextNode[] => {
  let end = nodes.length;
  while (end > 0) {
    const node = nodes[end - 1];
    if (node === undefined) break;
    if (node.type === "hardBreak" || (node.type === "text" && (node.text ?? "").trim() === "")) {
      end -= 1;
      continue;
    }
    break;
  }
  return nodes.slice(0, end);
};

const hasVisibleContent = (nodes: readonly RichTextNode[]): boolean =>
  nodes.some((node) => (node.text ?? "").trim() !== "" || (node.content?.length ?? 0) > 0);

const structuralTextFor = (node: RichTextNode): string => {
  if (node.type === "text") return node.text ?? "";
  if (node.type === "hardBreak") return "\n";
  return "";
};

const paragraphText = (nodes: readonly RichTextNode[]): string =>
  nodes.map(structuralTextFor).join("");

const paragraphStartsWithStructuralMarker = (paragraph: RichTextNode): boolean =>
  paragraph.type === "paragraph" && STRUCTURAL_START_RE.test(paragraphText(paragraph.content ?? []));

const paragraphStartsWithLayoutSpace = (paragraph: RichTextNode): boolean =>
  paragraph.type === "paragraph" && LEADING_LAYOUT_SPACE_RE.test(paragraphText(paragraph.content ?? []));

const paragraphHasHardBreak = (paragraph: RichTextNode): boolean =>
  paragraph.type === "paragraph" && (paragraph.content ?? []).some((node) => node.type === "hardBreak");

const structuralBreakRanges = (text: string): StructuralBreakRange[] =>
  Array.from(text.matchAll(STRUCTURAL_BREAK_RE), (match) => {
    const start = match.index ?? 0;
    return { start, end: start + match[0].length };
  });

const splitParagraphAtStructuralBreaks = (paragraph: RichTextNode): RichTextNode[] => {
  if (paragraph.type !== "paragraph" || paragraph.content === undefined) return [paragraph];

  const breaks = structuralBreakRanges(paragraphText(paragraph.content));
  if (breaks.length === 0) {
    return [{ ...paragraph, content: trimTrailingLayoutBreaks(normalizeInlineLineBreaks(paragraph.content)) }];
  }

  const out: RichTextNode[] = [];
  let current: RichTextNode[] = [];
  let streamOffset = 0;
  let breakIndex = 0;

  const pushCurrent = () => {
    const content = trimTrailingLayoutBreaks(current);
    if (hasVisibleContent(content)) out.push({ ...paragraph, content });
    current = [];
  };

  const appendTextSlice = (node: RichTextNode, text: string) => {
    appendTextWithHardBreaks(current, node, text);
  };

  const skipCompletedBreaks = (position: number) => {
    while (breakIndex < breaks.length) {
      const structuralBreak = breaks[breakIndex];
      if (structuralBreak === undefined || position < structuralBreak.end) return;
      breakIndex++;
    }
  };

  for (const child of paragraph.content) {
    if (child.type === "hardBreak") {
      skipCompletedBreaks(streamOffset);
      const structuralBreak = breaks[breakIndex];
      if (structuralBreak !== undefined && streamOffset >= structuralBreak.start && streamOffset < structuralBreak.end) {
        pushCurrent();
        streamOffset += 1;
        if (streamOffset >= structuralBreak.end) breakIndex++;
        continue;
      }

      current.push(child);
      streamOffset += 1;
      continue;
    }

    if (child.type !== "text" || child.text === undefined) {
      current.push(child);
      continue;
    }

    let localOffset = 0;
    while (localOffset < child.text.length) {
      const absoluteOffset = streamOffset + localOffset;
      skipCompletedBreaks(absoluteOffset);

      const structuralBreak = breaks[breakIndex];
      if (structuralBreak === undefined || absoluteOffset < structuralBreak.start) {
        const nextBreakStart = structuralBreak?.start ?? streamOffset + child.text.length;
        const sliceEnd = Math.min(streamOffset + child.text.length, nextBreakStart);
        appendTextSlice(child, child.text.slice(localOffset, sliceEnd - streamOffset));
        localOffset = sliceEnd - streamOffset;
        continue;
      }

      pushCurrent();
      const breakEnd = Math.min(streamOffset + child.text.length, structuralBreak.end);
      localOffset = breakEnd - streamOffset;
      if (streamOffset + localOffset >= structuralBreak.end) breakIndex++;
    }

    streamOffset += child.text.length;
  }

  pushCurrent();
  return out.length > 0 ? out : [paragraph];
};

const shouldMergeContinuationParagraph = (previous: RichTextNode, next: RichTextNode): boolean =>
  previous.type === "paragraph" &&
  next.type === "paragraph" &&
  hasVisibleContent(previous.content ?? []) &&
  hasVisibleContent(next.content ?? []) &&
  !paragraphStartsWithStructuralMarker(next) &&
  (
    paragraphStartsWithStructuralMarker(previous) ||
    paragraphStartsWithLayoutSpace(next) ||
    paragraphHasHardBreak(previous)
  );

const mergeContinuationParagraphs = (nodes: readonly RichTextNode[]): RichTextNode[] => {
  const out: RichTextNode[] = [];

  for (const node of nodes) {
    const previous = out[out.length - 1];
    if (previous !== undefined && shouldMergeContinuationParagraph(previous, node)) {
      out[out.length - 1] = {
        ...previous,
        content: [
          ...trimTrailingLayoutBreaks(previous.content ?? []),
          { type: "hardBreak" },
          ...(node.content ?? []),
        ],
      };
      continue;
    }

    out.push(node);
  }

  return out;
};

const normalizeAmendmentMarkupNodes = (nodes: readonly RichTextNode[]): RichTextNode[] =>
  mergeContinuationParagraphs(nodes.flatMap(normalizeAmendmentMarkupNode));

const normalizeAmendmentMarkupNode = (node: RichTextNode): RichTextNode[] => {
  if (node.type === "paragraph") return splitParagraphAtStructuralBreaks(node);
  if (node.content === undefined) return [node];
  return [{ ...node, content: normalizeAmendmentMarkupNodes(node.content) }];
};

export const normalizeAmendmentMarkupLayout = (doc: RichTextDoc): RichTextDoc => ({
  ...doc,
  content: normalizeAmendmentMarkupNodes(doc.content ?? []),
});

export const amendmentMarkupDocJson = (doc: RichTextDoc): string =>
  JSON.stringify(normalizeAmendmentMarkupLayout(doc), null, 2);

export const segmentsToMarkupDoc = (segments: readonly DiffSegment[]): RichTextDoc => {
  const nodes: RichTextNode[] = [];
  for (const segment of segments) appendTextNode(nodes, segment.text, segment.kind);
  return normalizeAmendmentMarkupLayout({ type: "doc", content: [{ type: "paragraph", content: nodes }] });
};

export type SectionMarkup = {
  readonly segments: DiffSegment[];
  readonly doc: RichTextDoc;
  readonly hasChanges: boolean;
};

export type AmendmentDiffCopyState = "idle" | "copying" | "copied" | "failed";

export type AmendmentDiffCopyAction =
  | { readonly type: "copyStarted" }
  | { readonly type: "copySucceeded" }
  | { readonly type: "copyFailed" }
  | { readonly type: "reset" };

export const emptyAmendmentDiffCopyState = (): AmendmentDiffCopyState => "idle";

export const nextAmendmentDiffCopyState = (
  state: AmendmentDiffCopyState,
  action: AmendmentDiffCopyAction,
): AmendmentDiffCopyState => {
  switch (action.type) {
    case "copyStarted":
      return "copying";
    case "copySucceeded":
      return state === "copying" ? "copied" : state;
    case "copyFailed":
      return state === "copying" ? "failed" : state;
    case "reset":
      return "idle";
  }
};

export const amendmentDiffCopyInProgress = (state: AmendmentDiffCopyState): boolean => state === "copying";

const AMENDMENT_DIFF_COPY_LABELS = {
  idle: "Copy markup JSON",
  copying: "Copying...",
  copied: "Copied",
  failed: "Copy failed",
} satisfies Record<AmendmentDiffCopyState, string>;

export const amendmentDiffCopyButtonLabel = (state: AmendmentDiffCopyState): string =>
  AMENDMENT_DIFF_COPY_LABELS[state];

export type AmendmentDiffPreviewStatus = "empty" | "changed" | "unchanged";

export type AmendmentDiffPreviewInput = {
  readonly hasInput: boolean;
  readonly hasChanges: boolean;
};

export const amendmentDiffPreviewStatus = ({
  hasInput,
  hasChanges,
}: AmendmentDiffPreviewInput): AmendmentDiffPreviewStatus => {
  if (!hasInput) return "empty";
  return hasChanges ? "changed" : "unchanged";
};

const AMENDMENT_DIFF_PREVIEW_LABELS = {
  empty: "Empty",
  changed: "Changes detected",
  unchanged: "No changes detected",
} satisfies Record<AmendmentDiffPreviewStatus, string>;

export const amendmentDiffPreviewLabel = (status: AmendmentDiffPreviewStatus): string =>
  AMENDMENT_DIFF_PREVIEW_LABELS[status];

const sectionMarkup = (segments: readonly DiffSegment[], doc: RichTextDoc): SectionMarkup => ({
  segments: [...segments],
  doc: normalizeAmendmentMarkupLayout(doc),
  hasChanges: segments.some((segment) => segment.kind !== "equal"),
});

export const segmentsToSectionMarkup = (segments: readonly DiffSegment[]): SectionMarkup =>
  sectionMarkup(segments, segmentsToMarkupDoc(segments));

export const buildTextMarkup = (previousText: string, amendedText: string): SectionMarkup =>
  segmentsToSectionMarkup(diffWords(previousText, amendedText));

type SegmentCursor = {
  readonly segments: readonly DiffSegment[];
  index: number;
  offset: number;
  lostSync: boolean;
};

const currentSegmentText = (cursor: SegmentCursor): string =>
  cursor.segments[cursor.index]?.text.slice(cursor.offset) ?? "";

const advanceCursor = (cursor: SegmentCursor, chars: number): void => {
  let remaining = chars;
  while (remaining > 0 && cursor.index < cursor.segments.length) {
    const text = currentSegmentText(cursor);
    if (text.length === 0) {
      cursor.index += 1;
      cursor.offset = 0;
      continue;
    }
    const take = Math.min(remaining, text.length);
    cursor.offset += take;
    remaining -= take;
    const segment = cursor.segments[cursor.index];
    if (segment === undefined || cursor.offset >= segment.text.length) {
      cursor.index += 1;
      cursor.offset = 0;
    }
  }
};

const appendPendingDeletions = (
  nodes: RichTextNode[],
  cursor: SegmentCursor,
  baseMarks?: readonly RichTextMark[],
): void => {
  while (cursor.index < cursor.segments.length) {
    const segment = cursor.segments[cursor.index];
    if (segment === undefined || segment.kind !== "delete") return;

    const text = currentSegmentText(cursor);
    if (text.length === 0) {
      cursor.index += 1;
      cursor.offset = 0;
      continue;
    }
    appendTextNode(nodes, text, "delete", baseMarks);
    advanceCursor(cursor, text.length);
  }
};

const leadingWhitespaceLength = (value: string): number => value.match(/^\s+/)?.[0].length ?? 0;

const commonPrefixLength = (a: string, b: string): number => {
  const max = Math.min(a.length, b.length);
  let i = 0;
  while (i < max && a[i] === b[i]) i += 1;
  return i;
};

const consumeForTextLeaf = (
  cursor: SegmentCursor,
  leafText: string,
  baseMarks: readonly RichTextMark[] | undefined,
): RichTextNode[] => {
  const nodes: RichTextNode[] = [];
  let remaining = leafText;

  while (remaining.length > 0) {
    const segment = cursor.segments[cursor.index];
    if (segment === undefined) {
      appendTextNode(nodes, remaining, "equal", baseMarks);
      break;
    }

    const segmentText = currentSegmentText(cursor);
    if (segmentText.length === 0) {
      cursor.index += 1;
      cursor.offset = 0;
      continue;
    }

    if (segment.kind === "delete") {
      appendPendingDeletions(nodes, cursor, baseMarks);
      continue;
    }

    if (!remaining.startsWith(segmentText)) {
      const leadingWhitespace = leadingWhitespaceLength(segmentText);
      if (leadingWhitespace > 0 && !remaining.startsWith(segmentText.slice(0, leadingWhitespace))) {
        // docToPlainText inserts block separators for matching. They are useful
        // for the diff, but they do not belong inside the cloned source nodes.
        advanceCursor(cursor, leadingWhitespace);
        continue;
      }
      if (segmentText.trim() === "") {
        advanceCursor(cursor, segmentText.length);
        continue;
      }
    }

    let take = "";
    if (remaining.startsWith(segmentText)) {
      take = segmentText;
    } else if (segmentText.startsWith(remaining)) {
      take = remaining;
    } else {
      const prefixLength = commonPrefixLength(remaining, segmentText);
      if (prefixLength === 0) {
        // If the token stream and rich-text leaf boundaries diverge, keep the
        // amended document text visible instead of dropping or reordering it.
        appendTextNode(nodes, remaining, "equal", baseMarks);
        cursor.lostSync = true;
        break;
      }
      take = remaining.slice(0, prefixLength);
    }

    appendTextNode(nodes, take, segment.kind, baseMarks);
    remaining = remaining.slice(take.length);
    advanceCursor(cursor, take.length);
  }

  return nodes;
};

const cloneWithMarkup = (node: RichTextNode, cursor: SegmentCursor): RichTextNode[] => {
  if (node.type === "text") return consumeForTextLeaf(cursor, node.text ?? "", node.marks);

  if (node.content === undefined) return [{ ...node }];

  const content: RichTextNode[] = [];
  for (const child of node.content) {
    appendPendingDeletions(content, cursor);
    content.push(...cloneWithMarkup(child, cursor));
  }
  appendPendingDeletions(content, cursor);
  return [{ ...node, content }];
};

const drainRemainingDeletions = (cursor: SegmentCursor): RichTextNode[] => {
  const nodes: RichTextNode[] = [];
  while (cursor.index < cursor.segments.length) {
    const segment = cursor.segments[cursor.index];
    if (segment === undefined) break;
    const text = currentSegmentText(cursor);
    if (text.length === 0) {
      cursor.index += 1;
      cursor.offset = 0;
      continue;
    }
    if (segment.kind === "delete") appendTextNode(nodes, text, "delete");
    advanceCursor(cursor, text.length);
  }
  return nodes;
};

const segmentsToMarkupDocPreservingAmendedFormat = (
  amended: RichTextDoc,
  segments: readonly DiffSegment[],
): RichTextDoc => {
  const cursor: SegmentCursor = { segments, index: 0, offset: 0, lostSync: false };
  const content: RichTextNode[] = [];
  for (const node of amended.content ?? []) content.push(...cloneWithMarkup(node, cursor));

  if (cursor.lostSync) return segmentsToMarkupDoc(segments);

  const tailDeletions = drainRemainingDeletions(cursor);
  if (tailDeletions.length > 0) content.push({ type: "paragraph", content: tailDeletions });

  return content.length > 0 ? normalizeAmendmentMarkupLayout({ type: "doc", content }) : segmentsToMarkupDoc(segments);
};

/** Diff a section's previous content against its edited content. */
export const buildSectionMarkup = (
  previous: RichTextDoc,
  next: RichTextDoc,
): SectionMarkup => {
  const segments = diffWords(docToPlainText(previous), docToPlainText(next));
  return {
    segments,
    doc: segmentsToMarkupDocPreservingAmendedFormat(next, segments),
    hasChanges: segments.some((segment) => segment.kind !== "equal"),
  };
};

export const buildSectionMarkupFromText = (
  previousText: string,
  amendedText: string,
): SectionMarkup => buildSectionMarkup(textDoc(previousText), textDoc(amendedText));

export type AmendmentDiffToolViewInput = {
  readonly previous: RichTextDoc;
  readonly amended: RichTextDoc;
};

export type AmendmentDiffToolView = {
  readonly markup: SectionMarkup;
  readonly doc: RichTextDoc;
  readonly hasInput: boolean;
  readonly canCopy: boolean;
  readonly previewStatus: AmendmentDiffPreviewStatus;
  readonly serializedDoc: string;
};

export const amendmentDiffToolView = ({
  previous,
  amended,
}: AmendmentDiffToolViewInput): AmendmentDiffToolView => {
  const markup = buildSectionMarkup(previous, amended);
  const doc = normalizeAmendmentMarkupLayout(markup.doc);
  const hasInput = docToPlainText(previous).trim() !== "" || docToPlainText(amended).trim() !== "";
  const canCopy = hasInput && docToPlainText(doc).trim() !== "";
  return {
    markup,
    doc,
    hasInput,
    canCopy,
    previewStatus: amendmentDiffPreviewStatus({ hasInput, hasChanges: markup.hasChanges }),
    serializedDoc: amendmentMarkupDocJson(doc),
  };
};
